import { AppState } from 'app/store';
import { getEntityOfTypeOrThrow } from './next';
import { Brand, BrandDomain, BrandSiteFeature, Curriculum, OrganizationSiteFeature } from 'app/constants/schemas';
import { Entity, EntityStringId } from 'app/types/entity';
import { defaultMemoize } from 'reselect';
import { Language } from 'app/constants/enums/Language';
import { NO_FEATURES_ENABLED, ConfigurationForSiteFeatures } from 'app/constants/schemas/SiteFeatures/siteFeatureTypes';
import { Configuration } from 'app/constants/schemas/SiteFeatures/Configuration';
import { ALL_SITE_FEATURES } from 'app/constants/enums/SiteFeature';
import { getBrandFeature, getOrganizationFeature } from '../actions/organization';
import * as OptionFP from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/function';
import { SiteFeatureId } from 'app/constants/enums/SiteFeature/data';
import { ICurriculum } from 'app/constants/schemas/Curriculum';

export const getDefaultDomain = defaultMemoize((state: AppState, brandId: EntityStringId): BrandDomain => {
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const domains = brand.domains.map(dId => getEntityOfTypeOrThrow(state, BrandDomain, dId));
  const defaultDomain = domains.find(d => d.default);
  // Writing partial functions like this is disappointing, but there's not a good way around this in general
  // without better types in the View Model Client.
  if (defaultDomain === undefined) {
    throw new Error(`Expected Brand: ${brandId} to have a default domain`);
  }
  return defaultDomain;
});

export interface IBrandInfo {
  brand: Brand;
  defaultDomain: BrandDomain;
}

export const getBrandInfo = defaultMemoize((state: AppState, brandId: EntityStringId): IBrandInfo => {
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const defaultDomain = getDefaultDomain(state, brandId);

  return ({
    brand,
    defaultDomain,
  });
});

export const getAllBrandsInfo = defaultMemoize((state: AppState, brandIds: EntityStringId[]): IBrandInfo[] => {
  const brandInfos = brandIds.map((bId) => getBrandInfo(state, bId));
  return brandInfos;
});

export const getActiveSiteFeatures = (state: AppState, brandId: EntityStringId) => {
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);

  const features: ConfigurationForSiteFeatures = { ...NO_FEATURES_ENABLED };
  ALL_SITE_FEATURES.forEach(feature => {
    const config = pipe(
      getBrandFeature(state, brandId, feature.id),
      OptionFP.alt((): OptionFP.Option<BrandSiteFeature | OrganizationSiteFeature> =>
        getOrganizationFeature(state, brand.organization, feature.id),
      ),
      OptionFP.map((f: BrandSiteFeature | OrganizationSiteFeature) => {
        const configEntity =
          f.configuration === null ? null : getEntityOfTypeOrThrow(state, Configuration, f.configuration);
        return {
          configuration: configEntity as any,
          enabled: f instanceof OrganizationSiteFeature || (f instanceof BrandSiteFeature && f.enabled),
        };
      }),
      OptionFP.getOrElse((): ConfigurationForSiteFeatures[SiteFeatureId] => ({ configuration: null, enabled: false })),
    );
    features[feature.id] = config as any;
  });

  return features;
};
/**
 * Attempt to guess which curricula is preferred based on the brand and an optional language input.
 * @param state -- The redux state
 * @param brandId -- The id of the brand containing some curricula.
 * @param language -- An optional language which indicates that curricula with that language should be preferred.
 * @returns curriculum -- The curriculum that most closely matched the request. If no language is given or if
 * the given language could not be matched, then the default curriculum is returned. If there is no default
 * curriculum then undefined is returned.
 */
export const getBrandPreferredCurriculum = (
  state: AppState,
  brandId: EntityStringId,
  language?: Language,
): Entity<ICurriculum> | undefined => {
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const curricula = brand.curricula.map(cId => getEntityOfTypeOrThrow(state, Curriculum, cId));
  // If there is a language argument, look for it.
  const preferred = language && curricula.find(c => c.language === language);
  // If there wasn't a language argument match, find the default
  const selected = preferred ?? curricula.find(c => c.isDefault);
  return selected;
};
